// @ts-check
// Nova conversa no painel lateral: e-mail (para, assunto, mensagem) ou WhatsApp (telefone e mensagem, ou um modelo
// aprovado). No WhatsApp a mensagem livre exige a janela de 24 horas aberta; com SERVICE_WINDOW_CLOSED o painel passa
// sozinho para "Modelo aprovado", que começa a conversa com quem nunca escreveu (POST /rest/conversations com
// templateId), com o atalho "Criar modelo" ao lado (contrato 08 de leads e WhatsApp, seção 7).
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { createConversation, listChannels } from '../../data/conversations.js';
import { currentWorkspaceMemberState, objectsByNameSingular } from '../../data/metadata-store.js';
import { findOneRecord } from '../../data/rest.js';
import { PendingAttachmentList, usePendingAttachments } from './message-composer.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { isValidEmail, isValidPhoneInput, toPhoneHandle } from '../../lib/conversation-format.js';
import { Icon } from '../../ui/icon.js';
import { Button, IconButton, LightIconButton } from '../../ui/button.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { closeSidePanel } from '../side-panel/side-panel-state.js';
import { ChannelIcon } from './conversation-display.js';
import { SenderAccountSelect } from '../calendar/sender-account-select.js';
import { CreateTemplateShortcut } from '../settings/whatsapp-templates/create-template-shortcut.js';
import { channelLabel, isTemplateReady, suggestTemplateValues, trimTemplateValues } from '../../lib/whatsapp-templates.js';
import { TemplatePicker, useMarketingBlock, useSendableTemplates } from './template-picker.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/integrations.js').WhatsappTemplate} WhatsappTemplate
 * @typedef {import('../../data/conversations.js').Channel} Channel
 */

/**
 * Modelos aprovados do número escolhido, com as variáveis e a prévia; marketing só para pessoa que pode receber.
 * @param {{ channelId: string | null, selected: WhatsappTemplate | null, values: Record<string, string>, personId?: string | null,
 *   onSelect: (template: WhatsappTemplate | null) => void, onValuesChange: (values: Record<string, string>) => void }} props
 */
function NewConversationTemplate({ channelId, selected, values, personId = null, onSelect, onValuesChange }) {
  const templates = useSendableTemplates(channelId);
  const marketingBlock = useMarketingBlock(personId);
  return html`<div class="form-field new-conversation-template">
    <span class="form-field-label"><${Icon} name="IconClipboardText" size=${14} /><span>${t('conversations.newPanel.template')}</span></span>
    <${TemplatePicker} templates=${templates} selectedId=${selected?.id ?? ''} values=${values} onValuesChange=${onValuesChange} marketingBlock=${marketingBlock}
      onSelect=${(/** @type {string} */ templateId) => onSelect(templates?.find((template) => template.id === templateId) ?? null)} />
  </div>`;
}

/**
 * @param {{ defaultChannelType?: 'WHATSAPP' | 'EMAIL', defaultTo?: { WHATSAPP?: string, EMAIL?: string }, personId?: string | null,
 *   onCreated?: (thread: import('../../data/conversations.js').ConversationThread) => void }} props
 */
export function NewConversationPanel({ defaultChannelType = 'EMAIL', defaultTo = {}, personId = null, onCreated }) {
  const [channelType, setChannelType] = useState(defaultChannelType);
  const [recipients, setRecipients] = useState({ WHATSAPP: defaultTo.WHATSAPP ?? '', EMAIL: defaultTo.EMAIL ?? '' });
  const [subject, setSubject] = useState('');
  // conta conectada que envia o e-mail; vazio usa o endereço da instituição (contrato 08 da agenda)
  const [connectedAccountId, setConnectedAccountId] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSending, setIsSending] = useState(false);
  // WhatsApp: mensagem livre (janela aberta) ou modelo aprovado (primeiro contato ou janela fechada)
  const [whatsappMode, setWhatsappMode] = useState(/** @type {'TEXT' | 'TEMPLATE'} */ ('TEXT'));
  const [whatsappChannels, setWhatsappChannels] = useState(/** @type {Channel[] | null} */ (null));
  const [whatsappChannelId, setWhatsappChannelId] = useState('');
  const [template, setTemplate] = useState(/** @type {WhatsappTemplate | null} */ (null));
  const [templateValues, setTemplateValues] = useState(/** @type {Record<string, string>} */ ({}));
  const [contactName, setContactName] = useState('');
  const to = recipients[channelType];
  // anexos no e-mail, pelo endereço da escola ou pela conta conectada (contrato 08 de e-mail avançado, seção 4)
  const pending = usePendingAttachments();
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));

  // aberto pelo card: o destinatário vem do contato principal (e-mail e celular da pessoa)
  useEffect(() => {
    if (!personId || defaultTo.EMAIL || defaultTo.WHATSAPP) return;
    const personObject = objectsByNameSingular.value.get('person');
    if (!personObject) return;
    findOneRecord(personObject, personId, { depth: 0 }).then((person) => {
      setContactName(String(person?.name?.firstName ?? ''));
      const email = person?.emails?.primaryEmail ?? '';
      const phone = person?.phones?.primaryPhoneNumber ? `${person.phones.primaryPhoneCallingCode ?? '+55'}${person.phones.primaryPhoneNumber}` : '';
      setRecipients((current) => ({ EMAIL: current.EMAIL || email, WHATSAPP: current.WHATSAPP || phone }));
      if (!email && phone) setChannelType('WHATSAPP');
    }).catch(() => {});
  }, [personId]);

  // números de WhatsApp da escola: com mais de um, a pessoa escolhe de qual sai o modelo
  useEffect(() => {
    if (channelType !== 'WHATSAPP' || whatsappChannels !== null) return;
    listChannels()
      .then((data) => {
        const active = data.channels.filter((channel) => channel.type === 'WHATSAPP' && channel.isActive !== false);
        setWhatsappChannels(active);
        setWhatsappChannelId((current) => current || active[0]?.id || '');
      })
      .catch(() => setWhatsappChannels([]));
  }, [channelType]);

  /** @param {WhatsappTemplate | null} chosen */
  const chooseTemplate = (chosen) => {
    setTemplate(chosen);
    setTemplateValues(chosen ? suggestTemplateValues(chosen, { contactName, memberName: currentWorkspaceMemberState.value?.name?.firstName }) : {});
    setError(null);
  };

  const sendTemplate = async () => {
    if (!template) return setError(t('conversations.newPanel.templateRequired'));
    if (!isTemplateReady(template, templateValues)) return setError(t('conversations.newPanel.templateVariablesRequired'));
    setError(null);
    setIsSending(true);
    try {
      const { thread } = await createConversation({
        channelType: 'WHATSAPP', to: toPhoneHandle(to), templateId: template.id, variables: trimTemplateValues(templateValues), channelId: template.channelId,
        ...(personId ? { personId } : {}),
      });
      showSuccessSnackbar(t('leadCapture.templateMessage.sentSnackbar'));
      closeSidePanel();
      if (thread) onCreated?.(thread);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsSending(false);
    }
  };

  const submit = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (channelType === 'EMAIL' && !isValidEmail(to)) return setError(t('conversations.newPanel.invalidEmail'));
    if (channelType === 'WHATSAPP' && !isValidPhoneInput(to)) return setError(t('conversations.newPanel.invalidPhone'));
    if (channelType === 'WHATSAPP' && whatsappMode === 'TEMPLATE') return sendTemplate();
    const uploaded = channelType === 'EMAIL' ? pending.getUploaded() : [];
    if (!text.trim() && uploaded.length === 0) return setError(t('conversations.newPanel.required'));
    if (pending.isUploading) return;
    setError(null);
    setIsSending(true);
    try {
      const { thread } = await createConversation({
        channelType,
        to: channelType === 'WHATSAPP' ? toPhoneHandle(to) : to.trim().toLowerCase(),
        ...(channelType === 'EMAIL' && subject.trim() ? { subject: subject.trim() } : {}),
        ...(channelType === 'EMAIL' && connectedAccountId ? { connectedAccountId } : {}),
        text: text.trim(),
        ...(uploaded.length > 0 ? { fileIds: uploaded.map((file) => file.fileId) } : {}),
        ...(personId ? { personId } : {}),
      });
      showSuccessSnackbar(t('conversations.sent'));
      closeSidePanel();
      if (thread) onCreated?.(thread);
    } catch (requestError) {
      // janela fechada: o painel passa para o modelo aprovado, sem perder o texto digitado
      if (channelType === 'WHATSAPP' && requestError instanceof ApiError && requestError.code === 'SERVICE_WINDOW_CLOSED') {
        setWhatsappMode('TEMPLATE');
        setError(t('conversations.newPanel.windowClosed'));
        return;
      }
      // A mensagem da API complementa o código quando não há tradução específica.
      const isKnown = requestError instanceof ApiError && requestError.userMessage !== t('errors.UNKNOWN');
      setError(isKnown || !(requestError instanceof ApiError) ? getErrorMessage(requestError) : requestError.messages[0] ?? getErrorMessage(requestError));
    } finally {
      setIsSending(false);
    }
  };

  // algo digitado ou escolhido: o atalho "Criar modelo" abre em nova aba para o painel não se perder
  const hasDraft = text.trim() !== '' || recipients.WHATSAPP.trim() !== '' || recipients.EMAIL.trim() !== '' || subject.trim() !== ''
    || pending.attachments.length > 0 || template !== null;

  return html`<form class="side-panel-page" onSubmit=${submit}>
    <div class="side-panel-top-bar">
      <div class="side-panel-top-bar-left">
        <span class="side-panel-page-chip">
          <${Icon} name="IconMessageCirclePlus" size=${16} />
          <span>${t('conversations.newPanel.title')}</span>
        </span>
      </div>
      <${IconButton} icon="IconX" size="small" variant="tertiary" ariaLabel=${t('sidePanel.close')} onClick=${closeSidePanel} />
    </div>
    <div class="side-panel-content create-record-form">
      <div class="form-field">
        <span class="form-field-label"><${Icon} name="IconMessages" size=${14} /><span>${t('conversations.newPanel.channel')}</span></span>
        <div class="segmented-control" role="tablist">
          ${/** @type {const} */ (['EMAIL', 'WHATSAPP']).map((type) => html`<button
            type="button"
            key=${type}
            role="tab"
            class="segmented-item"
            aria-selected=${channelType === type ? 'true' : 'false'}
            data-active=${channelType === type ? 'true' : undefined}
            onClick=${() => {
              setChannelType(type);
              setError(null);
            }}
          ><${ChannelIcon} channelType=${type} size=${14} />${t(`conversations.channels.${type}`)}</button>`)}
        </div>
      </div>
      <label class="form-field">
        <span class="form-field-label"><${Icon} name=${channelType === 'EMAIL' ? 'IconMail' : 'IconPhone'} size=${14} /><span>${t('conversations.newPanel.to')}</span></span>
        <input
          class="text-input"
          type=${channelType === 'EMAIL' ? 'email' : 'tel'}
          value=${to}
          autofocus
          placeholder=${channelType === 'EMAIL' ? t('conversations.newPanel.toEmailPlaceholder') : t('conversations.newPanel.toPhonePlaceholder')}
          onInput=${(event) => setRecipients({ ...recipients, [channelType]: /** @type {HTMLInputElement} */ (event.currentTarget).value })}
        />
      </label>
      ${channelType === 'EMAIL' ? html`<${SenderAccountSelect} value=${connectedAccountId} onChange=${setConnectedAccountId} />` : null}
      ${channelType === 'WHATSAPP'
        ? html`<div class="form-field">
            <span class="form-field-label"><${Icon} name="IconMessageCircle" size=${14} /><span>${t('conversations.newPanel.whatsappStart')}</span></span>
            <div class="segmented-control" role="tablist">
              ${/** @type {const} */ (['TEXT', 'TEMPLATE']).map((mode) => html`<button type="button" key=${mode} role="tab" class="segmented-item"
                aria-selected=${whatsappMode === mode ? 'true' : 'false'} data-active=${whatsappMode === mode ? 'true' : undefined}
                onClick=${() => {
                  setWhatsappMode(mode);
                  setError(null);
                }}>${t(`conversations.newPanel.whatsappModes.${mode}`)}</button>`)}
            </div>
          </div>`
        : null}
      ${channelType === 'WHATSAPP' && whatsappMode === 'TEMPLATE' && (whatsappChannels?.length ?? 0) > 1
        ? html`<label class="form-field">
            <span class="form-field-label"><${Icon} name="IconBrandWhatsapp" size=${14} /><span>${t('conversations.newPanel.number')}</span></span>
            <select class="text-input" value=${whatsappChannelId} onChange=${(/** @type {Event} */ event) => {
              setWhatsappChannelId(/** @type {HTMLSelectElement} */ (event.currentTarget).value);
              chooseTemplate(null);
            }}>
              ${(whatsappChannels ?? []).map((channel) => html`<option key=${channel.id} value=${channel.id} selected=${channel.id === whatsappChannelId}>${channelLabel(channel)}</option>`)}
            </select>
          </label>`
        : null}
      ${channelType === 'EMAIL'
        ? html`<label class="form-field">
            <span class="form-field-label"><${Icon} name="IconTypography" size=${14} /><span>${t('conversations.newPanel.subject')}</span></span>
            <input class="text-input" value=${subject} placeholder=${t('conversations.newPanel.subject')}
              onInput=${(event) => setSubject(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>`
        : null}
      ${channelType === 'WHATSAPP' && whatsappMode === 'TEMPLATE'
        ? html`<${NewConversationTemplate} channelId=${whatsappChannelId || null} selected=${template} values=${templateValues} personId=${personId}
            onSelect=${chooseTemplate} onValuesChange=${setTemplateValues} />`
        : html`<label class="form-field">
            <span class="form-field-label"><${Icon} name="IconAlignLeft" size=${14} /><span>${t('conversations.newPanel.message')}</span></span>
            <textarea class="text-input new-conversation-message" rows="6" value=${text} placeholder=${t('conversations.newPanel.messagePlaceholder')}
              onInput=${(event) => setText(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
          </label>`}
      ${channelType === 'EMAIL'
        ? html`<div class="new-conversation-attachments">
            <${PendingAttachmentList} attachments=${pending.attachments} onRemove=${pending.removeAttachment} />
            <${LightIconButton} icon="IconPaperclip" size="medium" accent="tertiary" ariaLabel=${t('conversations.composer.attach')}
              onClick=${() => fileInputRef.current?.click()} />
            <input ref=${fileInputRef} type="file" multiple hidden onChange=${(event) => {
              const input = /** @type {HTMLInputElement} */ (event.currentTarget);
              pending.addFiles(input.files);
              input.value = '';
            }} />
          </div>`
        : null}
      ${channelType === 'WHATSAPP'
        ? html`<p class="new-conversation-hint"><${Icon} name="IconInfoCircle" size=${14} /><span>${whatsappMode === 'TEMPLATE' ? t('conversations.newPanel.templateHint') : t('conversations.newPanel.whatsappHint')}</span></p>
          <${CreateTemplateShortcut} channelId=${whatsappChannelId || null} hasDraft=${hasDraft} onBeforeNavigate=${closeSidePanel}
            prefix=${whatsappMode === 'TEMPLATE' ? t('whatsappTemplates.shortcut.notFound') : undefined}
            memberText=${whatsappMode === 'TEMPLATE' ? t('whatsappTemplates.shortcut.pickerMemberNote') : undefined} className="new-conversation-template-shortcut" />`
        : null}
      ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
    </div>
    <div class="side-panel-footer">
      <${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${closeSidePanel} />
      <${Button} title=${t('conversations.newPanel.send')} icon="IconSend" accent="blue" size="small" type="submit" isLoading=${isSending} />
    </div>
  </form>`;
}
